/**
 * Pure logic for `ss dev summary` (the dev-environment "cartouche") and the
 * readiness classification used by `ss dev wait`.
 *
 * NO I/O — the CLI wiring in `commands/dev.ts` reads the cache / session / live
 * ports and the admin credentials, then calls these builders + renderer. Keeping
 * this pure makes the card deterministic and unit-testable (the box is rendered
 * by the CLI, never re-typed by the model, so it can never drift or mis-align).
 */

// ── Readiness ──────────────────────────────────────────────────────────────

/**
 * A readiness probe is "serving" when the server returned ANY real HTTP status
 * (100–599). 0 (or anything out of range) means connection refused / network
 * error / not yet bound → keep waiting.
 *
 * This is the fix for the old bash probe, which did
 *   STATUS=$(curl ... -w '%{http_code}' ... || echo "000")
 * On a refused connection curl prints `000` AND exits non-zero, so `|| echo 000`
 * fired too → STATUS="000000", and `[ "$STATUS" != "000" ]` was TRUE → a false
 * "ready". Classifying by a real HTTP range removes that whole class of bug.
 */
export function isServingStatus(status: number): boolean {
  return Number.isInteger(status) && status >= 100 && status <= 599;
}

// ── Cartouche ────────────────────────────────────────────────────────────────

export interface DevSummaryInput {
  apiPort: number | null;
  webPort: number | null;
  launchProfile: string | null;
  backendRunning: boolean;
  frontendRunning: boolean;
  dbServer: string | null;
  dbName: string | null;
  adminEmail: string | null;
  adminPassword: string | null;
  adminSource: string | null;
  logDir: string | null;
  logEnabled: boolean;
  launcherLogDir: string | null;
  /** Short, project-relative form of logDir — shown INSIDE the box (relay-proof). Abs stays in JSON. */
  logsRel: string | null;
  /** Short, project-relative form of launcherLogDir — shown INSIDE the box. */
  consoleRel: string | null;
  /** Result of the login health probe. `probed:false` = not attempted (e.g. backend down). */
  health: { probed: boolean; ok: boolean; status: number; hint: string | null };
  warnings: string[];
}

export interface DevSummary {
  ok: boolean;
  backend: { url: string | null; running: boolean; profile: string | null };
  frontend: { url: string | null; running: boolean };
  scalar_url: string | null;
  database: { server: string | null; name: string | null };
  admin: { email: string | null; password: string | null; source: string | null };
  health: { probed: boolean; ok: boolean; status: number; hint: string | null };
  logs: { dir: string | null; enabled: boolean };
  launcher_log_dir: string | null;
  /** Project-relative log paths rendered inside the box (the absolute ones stay in `logs`/`launcher_log_dir`). */
  logsRel: string | null;
  consoleRel: string | null;
  warnings: string[];
}

export function buildSummary(input: DevSummaryInput): DevSummary {
  const backendUrl = input.apiPort ? `http://localhost:${input.apiPort}` : null;
  const frontendUrl = input.webPort ? `http://localhost:${input.webPort}` : null;
  const scalarUrl = input.apiPort ? `http://localhost:${input.apiPort}/scalar` : null;
  const ok =
    input.backendRunning && input.frontendRunning && (!input.health.probed || input.health.ok);
  return {
    ok,
    backend: { url: backendUrl, running: input.backendRunning, profile: input.launchProfile },
    frontend: { url: frontendUrl, running: input.frontendRunning },
    scalar_url: scalarUrl,
    database: { server: input.dbServer, name: input.dbName },
    admin: { email: input.adminEmail, password: input.adminPassword, source: input.adminSource },
    health: input.health,
    logs: { dir: input.logDir, enabled: input.logEnabled },
    launcher_log_dir: input.launcherLogDir,
    logsRel: input.logsRel,
    consoleRel: input.consoleRel,
    warnings: input.warnings,
  };
}

function statusFlag(running: boolean): string {
  return running ? '✓ up' : '✗ DOWN';
}

function formatDatabase(db: { server: string | null; name: string | null }): string {
  if (!db.server && !db.name) return 'not configured';
  return `${db.server ?? '?'} / ${db.name ?? '?'}`;
}

function formatHealth(
  health: DevSummary['health'],
  backendRunning: boolean
): string {
  if (!backendRunning) return '✗ backend not running';
  if (!health.probed) return '— not checked';
  if (health.ok) return `✓ login OK (HTTP ${health.status})`;
  return `✗ login failed (HTTP ${health.status || 0})`;
}

/**
 * Render the full cartouche (box + footer) as a string. ALWAYS renders — every
 * field present (URLs + database + login) regardless of whether a side is up, so
 * the user always gets the connection info and can see exactly what is down.
 */
export function renderSummary(summary: DevSummary, opts: { restarted?: boolean } = {}): string {
  const title = `SmartStack Dev Environment${opts.restarted ? ' (restarted)' : ''}`;

  const profile = summary.backend.profile ? `  (${summary.backend.profile})` : '';
  const adminSrc = summary.admin.source ? `  (← ${summary.admin.source})` : '';

  // null entry = a horizontal divider.
  const rows: Array<string | null> = [
    `Backend  : ${summary.backend.url ?? 'n/a'}   ${statusFlag(summary.backend.running)}${profile}`,
    `Frontend : ${summary.frontend.url ?? 'n/a'}   ${statusFlag(summary.frontend.running)}`,
  ];
  if (summary.scalar_url) rows.push(`Scalar   : ${summary.scalar_url}`);
  rows.push(null);
  rows.push(`Database : ${formatDatabase(summary.database)}`);
  rows.push(null);
  rows.push(`Admin    : ${summary.admin.email ?? 'absent'}${adminSrc}`);
  rows.push(`Password : ${summary.admin.password ?? 'absent'}`);
  rows.push(`Health   : ${formatHealth(summary.health, summary.backend.running)}`);

  // Log locations live INSIDE the box (short project-relative paths) so they survive
  // a relay that copies only the frame — the recurring "où sont les logs ?": the
  // footer kept being dropped. The dev servers are launched HIDDEN (detached via WMI),
  // so their stdout/stderr exist ONLY in the Console capture — always surface it.
  if (summary.logsRel || summary.consoleRel) {
    rows.push(null);
    if (summary.logsRel) {
      rows.push(`Logs     : ${summary.logsRel}  (${summary.logs.enabled ? 'Serilog, rolling daily' : 'file sink off → Console'})`);
    }
    if (summary.consoleRel) {
      rows.push(`Console  : ${summary.consoleRel}  ({backend,frontend}.{out,err}.log)`);
    }
  }

  let out = drawBox(title, rows);

  // Footer — only the pointers + warnings (the paths now live in the box above).
  if (!summary.backend.running && summary.consoleRel) {
    out += `\n  ↳ backend not responding — check ${summary.consoleRel}/backend.err.log`;
  }
  if (summary.backend.running && summary.health.probed && !summary.health.ok && summary.health.hint) {
    out += `\n  ↳ health: ${summary.health.hint}`;
  }
  for (const w of summary.warnings) out += `\n⚠ ${w}`;

  return out;
}

/**
 * Draw a Unicode box that auto-sizes to its widest line. `contentLines` entries
 * that are `null` become a `╠═╣` divider; the title is centered. Box-drawing and
 * ✓/✗ glyphs are single-width, so `.length` is a faithful column count.
 */
function drawBox(title: string, contentLines: Array<string | null>): string {
  const textLines = contentLines.filter((l): l is string => l !== null);
  const inner = Math.max(title.length, ...textLines.map((l) => l.length)) + 2; // 1 space padding each side

  const top = '╔' + '═'.repeat(inner) + '╗';
  const divider = '╠' + '═'.repeat(inner) + '╣';
  const bottom = '╚' + '═'.repeat(inner) + '╝';

  const padLine = (l: string) => '║ ' + l + ' '.repeat(inner - 1 - l.length) + '║';
  const centerLine = (l: string) => {
    const total = inner - l.length;
    const left = Math.floor(total / 2);
    return '║' + ' '.repeat(left) + l + ' '.repeat(total - left) + '║';
  };

  const out: string[] = [top, centerLine(title), divider];
  for (const line of contentLines) {
    out.push(line === null ? divider : padLine(line));
  }
  out.push(bottom);
  return out.join('\n');
}
